/**
 * ImageReportsPanel — admin view of gallery-photo reports.
 *
 * Backed by GET /api/admin/image-reports; each row can be resolved or
 * dismissed. Every submission surfaces the offending image inline so
 * admins can act without leaving the page.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Flag, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const STATUS_TABS = [
  { key: "open", label: "Open" },
  { key: "resolved", label: "Resolved" },
  { key: "dismissed", label: "Dismissed" },
  { key: "", label: "All" },
];

export default function ImageReportsPanel() {
  const [reports, setReports] = useState([]);
  const [status, setStatus] = useState("open");
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      const { data } = await api.get("/admin/image-reports", { params });
      setReports(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load image reports");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const setReportStatus = async (r, next) => {
    try {
      setActingId(r.report_id);
      await api.patch(`/admin/image-reports/${r.report_id}`, { status: next });
      toast.success(`Report ${next}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update report");
    } finally {
      setActingId(null);
    }
  };

  const counts = useMemo(() => {
    const map = { open: 0, resolved: 0, dismissed: 0 };
    for (const r of reports) if (map[r.status] !== undefined) map[r.status] += 1;
    return map;
  }, [reports]);

  return (
    <Card className="p-4 border-border/60" data-testid="image-reports-panel">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Flag className="h-4 w-4 text-destructive" />
          <h2 className="text-sm font-semibold">Gallery image reports</h2>
          <span className="text-[11px] text-muted-foreground">
            Members flagging vendor photos. Take these seriously.
          </span>
        </div>
        <Button size="sm" variant="outline" className="rounded-full h-8" onClick={load} disabled={loading} data-testid="image-reports-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
          Refresh
        </Button>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        {STATUS_TABS.map((s) => (
          <Button
            key={s.label}
            size="sm"
            variant={status === s.key ? "default" : "outline"}
            onClick={() => setStatus(s.key)}
            className="rounded-full h-7"
            data-testid={`image-reports-tab-${s.label.toLowerCase()}`}
          >
            {s.label}
            {s.key && counts[s.key] > 0 && (
              <span className="ml-1.5 text-[10px] font-bold">{counts[s.key]}</span>
            )}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="p-10 text-center text-muted-foreground text-sm">
          <Loader2 className="h-5 w-5 animate-spin inline-block mr-2 align-middle" />
          Loading reports…
        </div>
      ) : reports.length === 0 ? (
        <div className="p-10 text-center text-sm text-muted-foreground italic" data-testid="image-reports-empty">
          No {status || "any"} image reports right now — good news.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="image-reports-grid">
          {reports.map((r) => (
            <Card key={r.report_id} className="p-3 border-border/60 flex flex-col" data-testid={`image-report-${r.report_id}`}>
              <div className="relative aspect-video overflow-hidden rounded-lg border border-border/60 bg-muted mb-2">
                <img
                  src={r.image_url}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 h-full w-full object-cover scale-110 blur-2xl opacity-60"
                />
                <img src={r.image_url} alt="" className="relative h-full w-full object-contain" />
                <Badge
                  className={`absolute top-1 right-1 text-[10px] uppercase tracking-wider ${
                    r.status === "open"
                      ? "bg-destructive text-destructive-foreground"
                      : r.status === "resolved"
                      ? "bg-emerald-600 text-white"
                      : "bg-muted-foreground text-white"
                  }`}
                >
                  {r.status}
                </Badge>
              </div>
              <div className="text-xs space-y-1 flex-1">
                <div className="text-[11px] uppercase tracking-widest text-primary font-semibold">{r.category}</div>
                <div className="font-medium truncate">
                  Vendor: <a href={`/vendors/${r.vendor_id}`} target="_blank" rel="noreferrer" className="underline">{r.vendor_name || r.vendor_id}</a>
                </div>
                <div className="text-muted-foreground">
                  By <strong className="text-foreground">{r.reporter_name}</strong> · {r.reporter_email}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {new Date(r.created_at).toLocaleString()}
                </div>
                {r.notes && (
                  <div className="mt-1 p-2 rounded bg-muted/70 text-[11px] italic">"{r.notes}"</div>
                )}
                {r.resolved_by_name && (
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Closed by {r.resolved_by_name}
                    {r.resolved_at ? ` · ${new Date(r.resolved_at).toLocaleString()}` : ""}
                  </div>
                )}
              </div>
              {r.status === "open" && (
                <div className="flex gap-1 mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setReportStatus(r, "resolved")}
                    disabled={actingId === r.report_id}
                    data-testid={`image-report-resolve-${r.report_id}`}
                    className="flex-1 h-7 border-emerald-500/60 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Resolve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setReportStatus(r, "dismissed")}
                    disabled={actingId === r.report_id}
                    data-testid={`image-report-dismiss-${r.report_id}`}
                    className="flex-1 h-7"
                  >
                    <XCircle className="h-3.5 w-3.5 mr-1" /> Dismiss
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </Card>
  );
}
